import React from "react";
import { Link } from "react-router-dom";

const API = process.env.REACT_APP_API_URL || "http://localhost:8000";

// Show "Only N left" when 3 or fewer remain? (the product page keeps this off by default)
export const SHOW_LOW_STOCK = false;

// Cloudinary: serve a small, modern-format copy instead of the full-size upload
export const cloudOpt = (url, w) => {
  if (!url || !url.includes("res.cloudinary.com")) return url;
  return url.replace("/upload/", `/upload/w_${w},f_auto,q_auto:good,dpr_auto/`);
};

export const getImgUrl = (img) => {
  if (!img) return "/images/s1.png";
  if (img.startsWith("http")) return cloudOpt(img, 420);
  if (img.startsWith("uploads/")) return `${API}/${img}`;
  return `${API}/uploads/${img}`;
};

export const availabilityOf = (p) => {
  if (p.availability) return p.availability;
  const stock = Number(p.stock) || 0;
  if (p.soldOut || stock <= 0) return "sold_out";
  return stock <= 3 ? "low_stock" : "in_stock";
};

export const CATEGORY_CSS = `
  .cp-wrap { background:#fdf6ec; min-height:60vh; padding:32px 40px 56px; font-family:'Segoe UI',sans-serif; }
  .cp-head { max-width:1320px; margin:0 auto 24px; display:flex; align-items:baseline; gap:12px; flex-wrap:wrap; }
  .cp-back { font-size:12px; color:#8a4a30; text-decoration:none; font-weight:600; letter-spacing:.5px; }
  .cp-back:hover { color:#5c2e1f; }
  .cp-title { font-family:Georgia,serif; font-size:26px; color:#4a2419; margin:0; font-weight:700; }
  .cp-count { background:#f2ddc4; color:#5c2e1f; font-size:12px; font-weight:700; padding:3px 12px; border-radius:20px; }
  .cp-grid { max-width:1320px; margin:0 auto; display:grid; grid-template-columns:repeat(auto-fill,minmax(220px,1fr)); gap:20px; }
  .cp-card { position:relative; background:#fff; border:1px solid #f2ddc4; border-radius:12px; overflow:hidden;
             text-decoration:none; color:inherit; display:block; box-shadow:0 2px 8px rgba(92,46,31,.05);
             transition:box-shadow .2s ease, transform .2s ease; }
  .cp-card:hover { box-shadow:0 8px 24px rgba(92,46,31,.12); transform:translateY(-3px); }
  .cp-img { width:100%; aspect-ratio:4/5; object-fit:cover; display:block; background:#f5ede0; }
  .cp-info { padding:12px 14px 14px; }
  .cp-name { font-family:Georgia,serif; font-size:14px; font-weight:700; color:#4a2419; line-height:1.35;
             display:-webkit-box; -webkit-line-clamp:2; -webkit-box-orient:vertical; overflow:hidden; min-height:38px; }
  .cp-price { margin-top:6px; font-weight:700; color:#5c2e1f; font-size:15px; }
  .cp-old { margin-left:8px; font-size:12px; color:#b0a090; text-decoration:line-through; font-weight:400; }
  .cp-soldout { position:absolute; top:10px; left:10px; background:rgba(198,40,40,.92); color:#fff; font-size:10px;
                font-weight:700; padding:3px 9px; border-radius:4px; letter-spacing:.5px; }
  .cp-avail { margin-top:6px; display:inline-flex; align-items:center; gap:6px; font-size:12px; font-weight:700; }
  .cp-avail i { width:8px; height:8px; border-radius:50%; background:currentColor; display:inline-block; }
  .cp-avail.in  { color:#2e7d32; }
  .cp-avail.low { color:#e65100; }
  .cp-avail.out { color:#c62828; }
  .cp-heart { position:absolute; top:10px; right:10px; width:34px; height:34px; border-radius:50%; border:none;
              background:rgba(255,255,255,.92); cursor:pointer; font-size:16px; color:#b8a898;
              display:flex; align-items:center; justify-content:center; box-shadow:0 1px 6px rgba(0,0,0,.15); }
  .cp-heart.on { color:#c62828; }
  .cp-msg { max-width:1320px; margin:60px auto; text-align:center; color:#8a4a30; font-size:15px; }
  .cp-btn { display:inline-block; margin-top:16px; background:#5c2e1f; color:#fff; text-decoration:none;
            padding:11px 28px; border-radius:8px; font-size:12px; font-weight:700; letter-spacing:1px; }
  @media (max-width:860px) { .cp-wrap { padding:20px 16px 40px; } .cp-grid { gap:14px; } }
  @media (max-width:540px) { .cp-grid { grid-template-columns:repeat(2,1fr); gap:10px; } .cp-title { font-size:21px; } }
`;

export function ProductCard({ p, wishlist = [], onToggleWishlist }) {
  const avail   = availabilityOf(p);
  const soldOut = avail === "sold_out";
  const wished  = wishlist.some((w) => (w?._id || w?.id || w) === p._id);

  return (
    <Link to={`/product/${p._id}`} className="cp-card">
      <img
        className="cp-img"
        src={getImgUrl(p.images?.[0])}
        alt={p.name}
        loading="lazy"
        decoding="async"
        onError={(e) => { e.target.onerror = null; e.target.src = "/images/s1.png"; }}
      />
      {soldOut && <span className="cp-soldout">SOLD OUT</span>}
      {onToggleWishlist && (
        <button
          type="button"
          className={`cp-heart${wished ? " on" : ""}`}
          aria-label="Toggle wishlist"
          onClick={(e) => { e.preventDefault(); e.stopPropagation(); onToggleWishlist(p); }}
        >
          ♥
        </button>
      )}
      <div className="cp-info">
        <div className="cp-name">{p.name}</div>
        <div className="cp-price">
          ₹{Number(p.price).toLocaleString()}
          {p.originalPrice > p.price && (
            <span className="cp-old">₹{Number(p.originalPrice).toLocaleString()}</span>
          )}
        </div>
        <div className={`cp-avail ${soldOut ? "out" : avail === "low_stock" && SHOW_LOW_STOCK ? "low" : "in"}`}>
          <i />
          {soldOut
            ? "Out of Stock"
            : avail === "low_stock" && SHOW_LOW_STOCK
              ? `Only ${Number(p.stock)} left`
              : "In Stock"}
        </div>
      </div>
    </Link>
  );
}